import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toasts } from "../../../../../common/Toast/Toast";
import Button from "../../../../../common/Button/Button";
import Select from "../../../../../common/Formik/Select/Select";
import InputFormik from "../../../../../common/Formik/Input/InputFormik";

import golden from "../../../../../../assets/logos/golden-logo.png";
import silver from "../../../../../../assets/logos/silver-logo.png";
import usdtIcon from "../../../../../../assets/icons/coins/usdt.svg";
import usdcIcon from "../../../../../../assets/icons/coins/usdc.svg";

import {
    getTokenBalanceAction,
    convertToStableCoinAction,
    getUserCoinListAction,
} from "../../../../../../features/user/userSlice";
import { Messages, FORMIK_REGEX, STATUS_CODE } from "../../../../../../utils/constants";
import { formatPrice, toFix } from "../../../../../../utils/utils";

import "./Convert.scss";

const assetOptions = [
    {
        label: "Gold STBL",
        icon: golden,
        value: 1,
        symbol: "XAU/USD",
    },
    {
        label: "Silver STBL",
        icon: silver,
        value: 2,
        symbol: "XAG/USD",
    },
];

const toOptions = [
    {
        label: "USDT",
        icon: usdtIcon,
        value: 3,
        symbol: "USDT/USD",
    },
    {
        label: "USDC",
        icon: usdcIcon,
        value: 4,
        symbol: "USDC/USD",
    },
    ...assetOptions,
];

const Convert = ({ onConvertSuccess }) => {
    const dispatch = useDispatch();
    const [coinLists, setCoinList] = useState([]);
    // Socket-fed prices: SPOT_PRICE → spotPrice, CRYPTO_PRICE → cryptoPrice, COIN_RATES → coinRates
    const cryptoPrices = useSelector((state) => state?.users?.cryptoPrice);
    const spotPrice = useSelector((state) => state?.users?.spotPrice);
    const coinRates = useSelector((state) => state?.users?.coinRates);
    const kycStatus = useSelector(
        (state) => state?.persist?.userInfo?.kyc_status
    );

    const [firstToken, setFirstToken] = useState(assetOptions[0]);
    const [secondToken, setSecondToken] = useState(toOptions[0]);
    const [tokenOneBalance, setTokenOneBalance] = useState(0);
    const [tokenTwoBalance, setTokenTwoBalance] = useState(0);
    const [approxTokens, setApproxTokens] = useState(0);
    const [decimalLimit, setDecimalLimit] = useState(8);
    const [newAssetOptions, setNewAssetOptions] = useState([]);
    const [cryptoAssetOptions, setCryptoAssetOptions] = useState(toOptions);

    const parseTokenQty = (qty) =>
        Array.isArray(qty) && qty.length === 0 ? 0 : Number(qty ?? 0);

    // Prefer API coin icons (same as wallet table), fallback to local USDT/USDC SVGs
    const buildToOptions = (coinList = []) => {
        const getCoinIcon = (symbol, fallback) =>
            coinList.find((c) => c.coin_symbol === symbol)?.image_path || fallback;

        return [
            {
                label: "USDT",
                icon: getCoinIcon("USDT", usdtIcon),
                value: 3,
                symbol: "USDT/USD",
            },
            {
                label: "USDC",
                icon: getCoinIcon("USDC", usdcIcon),
                value: 4,
                symbol: "USDC/USD",
            },
            ...assetOptions,
        ];
    };

    // Resolve live price for Gold/Silver (SPOT_PRICE) or crypto (CRYPTO_PRICE / COIN_RATES)
    const getAssetPrice = (token) => {
        if (!token) return 0;
        if (token.value === 1) return Number(spotPrice?.goldPrice || 0);
        if (token.value === 2) return Number(spotPrice?.silverPrice || 0);
        if (token.label === "ETH") return Number(cryptoPrices?.ethPrice || 0);
        if (token.label === "BTC") return Number(cryptoPrices?.btcPrice || 0);
        if (token.label === "USDT") return Number(coinRates?.usdtPrice || 0);
        if (token.label === "USDC") return Number(coinRates?.usdcPrice || 0);
        const coin = coinLists?.find((c) => c.coin_symbol === token.label);
        return Number(coin?.coinPrice || 0);
    };

    const formatCurrentPrice = (token) => {
        const price = getAssetPrice(token);
        return price ? formatPrice(price) : "—";
    };

    const convertValidationSchema = Yup.object().shape({
        quantity: Yup.string()
            .required("Required")
            .matches(FORMIK_REGEX.NONZERODECIMAL, Messages?.NONZERODECIMALVALIDATION)
            .test("checkBalance", "Quantity exceeds token balance!", (val) => {
                return Number(val) <= Number(tokenOneBalance);
            }),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            quantity: "",
        },
        validationSchema: convertValidationSchema,
        onSubmit: async (values) => {
            if (kycStatus !== "APPROVED") {
                toasts.error("KYC Verification is required to convert tokens.");
                return;
            }

            const targetCryptoMap = {
                1: "GOLD",
                2: "SILVER",
                3: "USDT",
                4: "USDC",
            };

            // Frontend Amount → body key `amount`
            const payload = {
                quantity: Number(values.quantity),
                sourceTokenId: firstToken.value,
                sourceTokenType: firstToken.value === 1 ? "GOLD" : "SILVER",
                targetCrypto: targetCryptoMap[secondToken.value],
            };

            try {
                const response = await dispatch(convertToStableCoinAction(payload));
                if (response.status === 200) {
                    formik.resetForm();
                    setApproxTokens(0);
                    getBalance();
                    fetchCoinOptions();
                    onConvertSuccess?.();
                }
            } catch (err) {
                console.error("Convert to stablecoin error:", err);
            }
        },
    });

    const fetchCoinOptions = async () => {
        try {
            const response = await dispatch(getUserCoinListAction());
            if (response.data.status === STATUS_CODE.successful) {
                const newData = response.data.data.coinList;
                setCoinList(newData);

                const updatedOptions = buildToOptions(newData);
                setCryptoAssetOptions(updatedOptions);

                // Keep selected "To" token icon in sync with API symbols
                const matchedTo = updatedOptions.find((t) => t.value === secondToken.value);
                if (matchedTo) setSecondToken(matchedTo);

                const quantityDecimal = newData.find(
                    (coin) => coin.coin_symbol === firstToken?.label
                );
                setDecimalLimit(quantityDecimal?.quantity_decimal || 8);
            }
        } catch (error) {
            console.error("Error fetching coin options:", error);
        }
    };

    const getBalance = async () => {
        try {
            // Gold & Silver STBL: GET /user/token/balance
            const goldBalanceRes = await dispatch(getTokenBalanceAction({ tokenId: 1 }));
            const silverBalanceRes = await dispatch(getTokenBalanceAction({ tokenId: 2 }));

            const goldBalance = parseTokenQty(goldBalanceRes?.data?.data?.quantity);
            const silverBalance = parseTokenQty(silverBalanceRes?.data?.data?.quantity);

            if (firstToken.value === 1) {
                setTokenOneBalance(goldBalance);
                setTokenTwoBalance(silverBalance);
            } else {
                setTokenOneBalance(silverBalance);
                setTokenTwoBalance(goldBalance);
            }
        } catch (error) {
            console.log("Error fetching token balance:", error);
        }
    };

    useEffect(() => {
        const list = cryptoAssetOptions.filter((t) => t.value !== firstToken.value);
        setNewAssetOptions(list);
        if (secondToken.value === firstToken.value) {
            setSecondToken(list[0]);
        }
    }, [firstToken, cryptoAssetOptions]);

    useEffect(() => {
        fetchCoinOptions();
        getBalance();
    }, [firstToken]);

    const checkConversion = () => {
        if (formik.values.quantity > 0) {
            const quantity = Number(formik.values.quantity);
            const fromPrice = getAssetPrice(firstToken);
            const toPrice = getAssetPrice(secondToken);

            if (fromPrice && toPrice) {
                setApproxTokens((fromPrice / toPrice) * quantity);
            } else {
                setApproxTokens(0);
            }
        } else {
            setApproxTokens(0);
        }
    };

    useEffect(() => {
        checkConversion();
    }, [formik.values.quantity, spotPrice, cryptoPrices, coinRates, firstToken, secondToken, coinLists]);

    const handleQuantityChange = (e) => {
        const filterVal = e.target.value.replace(/[^0-9.]/g, "");
        const regex = new RegExp(`^\\d*(\\.\\d{0,${decimalLimit}})?$`);
        if (filterVal === "" || regex.test(filterVal)) {
            formik.setFieldValue("quantity", filterVal);
        }
    };

    const getToBalanceLabel = () => {
        const isStbl = secondToken.value === 1 || secondToken.value === 2;
        const balance = isStbl
            ? tokenTwoBalance
            : coinLists?.find((coin) => coin.coin_symbol === secondToken.label)?.balance || 0;
        const unit = isStbl ? "ozt" : secondToken.label;
        return `${toFix(Number(balance), 3)} ${unit}`;
    };

    return (
        <div className="convert">
            <div className="convert_left">
                <div className="inner_select">
                    <Select
                        variant="xlarge_height"
                        className="select_time"
                        label="Convert From"
                        options={assetOptions}
                        value={firstToken}
                        onChange={(selected) => {
                            formik.setFieldValue("quantity", "");
                            setFirstToken(selected);
                        }}
                        isSearchable={false}
                    />
                    <div className="convert_meta">
                        <span className="spot_price">
                            Current Market Price: ${formatCurrentPrice(firstToken)}
                        </span>
                        <div className="balancedata">
                            Available Balance:{" "}
                            <span>{toFix(Number(tokenOneBalance), 3)} ozt</span>
                        </div>
                    </div>
                </div>
                <div className="right_inner">
                    <InputFormik
                        variant="large_height"
                        label="Amount to Convert"
                        placeholder="Enter Amount"
                        name="quantity"
                        value={formik.values.quantity}
                        onChange={handleQuantityChange}
                        onBlur={formik.handleBlur}
                        formik={formik}
                    />
                    {approxTokens > 0 && (
                        <div className="approx_output">
                            <label>Estimated Amount Received</label>
                            <p>
                                ≈{formatPrice(Number(approxTokens))}
                                <img src={secondToken.icon} alt={secondToken.label} />
                            </p>
                        </div>
                    )}
                </div>
                <div className="inner_select">
                    <Select
                        variant="xlarge_height"
                        className="select_time"
                        label="Convert To"
                        options={newAssetOptions}
                        value={secondToken}
                        onChange={(selected) => {
                            formik.setFieldValue("quantity", "");
                            setSecondToken(selected);
                        }}
                        isSearchable={false}
                    />
                    <div className="convert_meta">
                        <span className="spot_price">
                            Current Market Price: ${formatCurrentPrice(secondToken)}
                        </span>
                        <div className="balancedata">
                            Available Balance: <span>{getToBalanceLabel()}</span>
                        </div>
                    </div>
                </div>
            </div>
            <Button
                className="convert_btn"
                fluid
                onClick={formik.handleSubmit}
                disabled={
                    !formik.values.quantity ||
                    !!formik.errors.quantity ||
                    tokenOneBalance <= 0 ||
                    Number(formik.values.quantity) <= 0
                }
            >
                Convert
            </Button>
        </div>
    );
};

export default Convert;
